import { useCallback, useEffect, useState } from 'react';
import { parseApiError } from '../utils/apiError';

const DEFAULT_LOCK_SECONDS = 15 * 60;

/**
 * Tracks the 429 AUTH_THROTTLED lockout for an auth form.
 * While locked the BE rejects every attempt (even correct ones), so the form
 * should disable its submit button until `remainingSeconds` reaches 0.
 */
export function useAuthThrottle() {
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!lockedUntil) return;
    const timer = window.setInterval(() => {
      const t = Date.now();
      setNow(t);
      if (t >= lockedUntil) {
        setLockedUntil(null);
        setMessage(null);
      }
    }, 1000);
    return () => window.clearInterval(timer);
  }, [lockedUntil]);

  /** Returns true when the error was a throttle error (and the lock was started). */
  const handleError = useCallback((err: unknown): boolean => {
    const parsed = parseApiError(err);
    if (parsed.code !== 'AUTH_THROTTLED' && parsed.status !== 429) return false;
    const seconds = parsed.retryAfterSeconds ?? DEFAULT_LOCK_SECONDS;
    const t = Date.now();
    setNow(t);
    setLockedUntil(t + seconds * 1000);
    setMessage(parsed.message);
    return true;
  }, []);

  const reset = useCallback(() => {
    setLockedUntil(null);
    setMessage(null);
  }, []);

  const remainingSeconds = lockedUntil ? Math.max(0, Math.ceil((lockedUntil - now) / 1000)) : 0;

  return {
    isLocked: remainingSeconds > 0,
    remainingSeconds,
    countdown: formatCountdown(remainingSeconds),
    message,
    handleError,
    reset,
  };
}

export type AuthThrottle = ReturnType<typeof useAuthThrottle>;

export function formatCountdown(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}
